import type { TaskDto } from "../../services/ApiService";
import type { Task, TaskCreator } from "../../types/Task";
import TaskId from "../../state/TaskId";
import FormId from "../../state/FormId";
import { fromLegacyPlatformIcon } from "../iconUtil";
import SubmissionId from "../../state/SubmissionId";
import IntegrationId from "../../integrations/IntegrationId";
import { isNil } from "lodash-es";
import CustomerId from "../../state/CustomerId";
import ApiKeyId from "../../state/ApiKeyId";
import UserId from "../../state/UserId";

export const toTask = (dto: TaskDto): Task => {
  const taskUsers = dto.taskUsers.reduce<Record<string, string>>((acc, user) => {
    acc[user.userId] = user.username;
    return acc;
  }, {});

  return {
    id: TaskId.from(dto.id),
    customerId: CustomerId.from(`${dto.customerId}`),
    formId: FormId.from(dto.formId),
    formName: dto.formName,
    description: dto.description,
    formIcon: fromLegacyPlatformIcon(dto.formIcon, "document"),
    formIconColor: dto.formIconColor,
    users: dto.users.map((userId) => ({ userId: UserId.from(userId), username: taskUsers[userId] })),
    createdBy: ((): TaskCreator => {
      if (dto.createdBy.type === "USER") {
        return {
          id: !isNil(dto.createdBy?.id) ? UserId.from(dto.createdBy.id) : undefined,
          type: dto.createdBy.type,
          name: dto.createdBy.name,
        };
      } else if (dto.createdBy.type === "INTEGRATION") {
        return {
          id: !isNil(dto.createdBy?.id) ? IntegrationId.from(dto.createdBy.id) : undefined,
          type: dto.createdBy.type,
          name: dto.createdBy.name,
        };
      } else if (dto.createdBy.type === "API_KEY") {
        return {
          id: !isNil(dto.createdBy?.id) ? ApiKeyId.from(dto.createdBy.id) : undefined,
          type: dto.createdBy.type,
          name: dto.createdBy.name,
        };
      } else {
        return {
          type: "UNKNOWN",
        };
      }
    })(),
    message: dto.message,
    dates: {
      creationDate: new Date(dto.dates.creationDate),
      publishDate: new Date(dto.dates.publishDate),
      informationDate: !isNil(dto.dates?.informationDate) ? new Date(dto.dates.informationDate) : undefined,
    },
    data: dto.data,
    status: dto.status,
    fulfilments: dto.fulfilments.map(({ userId, date, formId, registrationId, draftSubmissionId }) => ({
      userId: UserId.from(userId),
      date: new Date(date),
      formId: FormId.from(formId),
      registrationId: !isNil(registrationId) ? SubmissionId.from(registrationId) : undefined,
      draftSubmissionId: draftSubmissionId ?? undefined,
    })),
    // Not returned by API versions without task transfers
    transfers: (dto.transfers ?? []).map(({ from, to, date, draftSubmissionId }) => ({
      from: { userId: UserId.from(from.userId), username: from.username },
      to: { userId: UserId.from(to.userId), username: to.username },
      date: new Date(date),
      draftSubmissionId: draftSubmissionId ?? undefined,
    })),
    location: dto.location,
  };
};
